import { Button, HeaderText } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { cn } from "@/lib/utils";
import {
  EllipsisVertical,
  LoaderCircle,
  Pencil,
  RotateCcwClock,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";

/**
 * EditableRecord Component
 *
 * A display/edit toggle component with a dropdown action menu to provide a consistent UX for viewing and
 * editing medical records. Shows a read-only display by default and switches to a form when editing.
 * Parent maintains the editing state and provides both the display and form components. Can also be used
 * to display history of the record or delete the record.
 *
 * ## Design Pattern
 *
 * This component follows a "controlled component" pattern where:
 * - Parent manages `isEditing` state via `useState`
 * - Parent passes both `displayComponent` (read-only) and `formComponent` (editable)
 * - The form component receives a callback to close editing (e.g., on save/cancel)
 * - Parent provides `onEdit` callback to enter edit mode from the dropdown
 * - If required, parent provides `onViewHistory` callback to view history from the dropdown
 * - If required, parent provides `onDelete` callback to delete the record from the dropdown
 *
 * ## Example Usage
 *
 * ```tsx
 * // In parent component
 * const [isEditing, setIsEditing] = useState(false);
 *
 * const getDisplayComponent = () => (
 *   <div className="flex gap-2">
 *     {data.myDataField}
 *   </div>
 * );
 *
 * const getFormComponent = () => (
 *   <MyEditor
 *     data={data}
 *     onSave={() => setIsEditing(false)}  // Close after saving
 *     onCancel={() => setIsEditing(false)}
 *   />
 * );
 *
 * return (
 *   <EditableRecord
 *     title="My Record"
 *     displayComponent={getDisplayComponent()}
 *     formComponent={getFormComponent()}
 *     isEditing={isEditing}
 *     onEdit={() => setIsEditing(true)}
 *   />
 * );
 * ```
 */
interface EditableRecordProps {
  containerClassName?: string;
  deletePending?: boolean;
  disabled?: boolean;
  displayComponent: ReactNode;
  formComponent: ReactNode;
  isEditing: boolean;
  onDelete?: () => void;
  onEdit: () => void;
  onViewHistory?: () => void;
  pending?: boolean;
  quickActions?: Array<{
    label: string;
    icon?: ReactNode;
    onClick: () => void;
  }>;
  title?: string;
}

export function EditableRecord({
  containerClassName,
  deletePending,
  disabled = false,
  displayComponent,
  formComponent,
  isEditing,
  onDelete,
  onEdit,
  onViewHistory,
  pending,
  quickActions,
  title,
}: EditableRecordProps) {
  return (
    <div
      className={cn(
        "flex flex-col relative gap-2 px-3 py-2 w-full",
        containerClassName,
      )}
      aria-label={`Edit ${title?.toLowerCase() || "record"}`}
    >
      {title && !isEditing && <HeaderText size="sm">{title}</HeaderText>}
      {isEditing ? formComponent : displayComponent}
      {!isEditing && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="icon"
              size="sm"
              aria-label={title ? `Options for ${title}` : "Options"}
              className="m-0 p-2 h-6 absolute right-0 top-1"
              disabled={disabled || pending}
            >
              {pending ? (
                <LoaderCircle className="text-link animate-spin" size={18} />
              ) : (
                <EllipsisVertical className="text-link" size={18} />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" alignOffset={10} sideOffset={0}>
            {quickActions?.map((action) => (
              <DropdownMenuItem key={action.label} onClick={action.onClick}>
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            ))}
            {quickActions && quickActions.length > 0 && (
              <DropdownMenuSeparator />
            )}
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            {onViewHistory && (
              <DropdownMenuItem onClick={onViewHistory}>
                <RotateCcwClock className="h-4 w-4 mr-2" />
                See History
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem variant="danger" onClick={onDelete}>
                {deletePending ? (
                  <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Void
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
